import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TreoAnimations } from 'tsui/@treo';
import { AccountService, AuthService } from 'app/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { switchMap, tap } from 'rxjs/operators';
import { noop, of, zip } from 'rxjs';
import { EMAIL_PATTERN, getSubdomain } from 'app/shared/Utils/common.utils';
import { environment } from '@env/environment';
import { AccountsService } from 'app/modules/accounts/accounts.service';
import { Store } from '@ngxs/store';
import { SetUser } from 'app/state/user/actions';
import { Permission } from 'app/core/enums';
import { PermissionValue } from '@core/models';

@Component({
  selector: 'auth-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: TreoAnimations,
})
export class AuthSignInComponent extends OnDestroyMixin implements OnInit {
  signInForm: UntypedFormGroup;
  message: any;
  redirectUri: string;
  ssoLoginUrl: string;
  canLoginWithPassword: boolean = false;
  canLoginWithSaml: boolean = false;
  samlRequired: boolean = false;
  hasRetrievedAuthenticationSettings: boolean = false;

  @ViewChild('passwordField') private passwordField: ElementRef;

  get passwordFieldIcon(): string {
    let icon = 'visibility';

    if (this.passwordFieldElement?.type === 'text') {
      icon += '_off';
    }

    return icon;
  }

  private get passwordFieldElement(): HTMLInputElement {
    return this.passwordField?.nativeElement as HTMLInputElement;
  }

  constructor(
    private _activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private _formBuilder: UntypedFormBuilder,
    private _router: Router,
    private readonly accountsService: AccountsService,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.handleInvalidInviteTokenMsg();

    this.signInForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      password: ['', Validators.required],
    });

    this.redirectUri = this._activatedRoute.snapshot.queryParamMap.get('redirect_uri');

    this._activatedRoute.queryParams.pipe(untilComponentDestroyed(this)).subscribe((params) => {
      const ttoken = params['ttoken'];
      if (ttoken) {
        this.authService
          .signInWithSamlTToken(ttoken)
          .pipe(
            untilComponentDestroyed(this),
            tap((res) => {
              this.store.dispatch(new SetUser(res.user));
              this._router.navigateByUrl('/accounts');
            }),
          )
          .subscribe(noop);
      }
    });

    this.ssoLoginUrl = `${
      environment.baseUrl
    }/integrations/saml/setup?subdomain=${getSubdomain()}&client_redirect_url=${encodeURIComponent(
      window.location.href,
    )}`;
  }

  private handleInvalidInviteTokenMsg(): void {
    const isInviteTokenValid = this._activatedRoute.snapshot.queryParamMap.get('invalidInviteToken');
    if (isInviteTokenValid) {
      this._showError('Your invitation link is invalid or has been used. Please contact your administrator.');
      this._router.navigate([], {
        queryParams: { invalidInviteToken: null, redirectURL: '/accounts' },
        queryParamsHandling: 'merge',
      });
    }
  }

  goHome(): void {
    window.open('https://www.termsheet.com');
  }

  // SC-19135 (22/09/2023) | Temporary disable sign up link.
  // goSignUp(): void {
  //   this._router.navigate(['sign-up'], { queryParams: { redirectURL: '/projects' } });
  // }

  signIn(): void {
    if (this.signInForm.invalid) {
      this._showError();
      return;
    }

    this.signInForm.disable();

    this.message = null;

    const credentials = this.signInForm.value;
    const subdomain = getSubdomain();

    this.authService
      .signIn(credentials)
      .pipe(
        untilComponentDestroyed(this),
        switchMap((response) => {
          const user = response.user;
          const accounts = user.accounts;
          this.accountsService.setAccounts(response.user.accounts);
          this.store.dispatch(new SetUser(response.user));

          if (subdomain) {
            const account = accounts.find((x) => x.subdomain === subdomain);

            return zip([of(response), this.accountService.getUser(+account.id)]);
          }

          return zip([of(response), of(null)]);
        }),
      )
      .subscribe({
        next: ([response, user]) => {
          if (!subdomain) {
            this._router.navigate(['accounts']);
            return;
          }

          // Set the redirect url.
          // The '/signed-in-redirect' is a dummy url to catch the request and redirect the user
          // to the correct page after a successful sign in. This way, that url can be set via
          // routing file and we don't have to touch here.
          const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || '/signed-in-redirect';

          // In the case of passing a redirect_uri in the query string, will
          // be coming from soem addin requesting auth token for use within the app
          if (this.redirectUri) {
            window.location.replace(`${this.redirectUri}?auth_token=${this.authService.accessToken}`);
          } else {
            // Navigate to the redirect url
            window.location.replace(redirectURL);
          }
        },
        error: (response) => {
          // Re-enable the form
          this.signInForm.enable();

          // Show the error message
          this._showError(response?.error?.user_authentication);
        },
      });
  }

  /**
   * Given an email address retrieve authentication settings
   */
  retrieveAuthenticationSettings() {
    let email = this.signInForm.get('email').value;
    if (email) {
      const subdomain = getSubdomain();
      this.authService
        .getUserAuthenticationSettings(email, subdomain)
        .pipe(
          untilComponentDestroyed(this),
          tap((res) => {
            if (res.authentication_settings) {
              this.canLoginWithSaml = res.authentication_settings.idp?.saml?.configured;
              this.samlRequired = res.authentication_settings.idp?.saml?.required;
              this.ssoLoginUrl = res.authentication_settings.idp?.saml?.url;
              this.hasRetrievedAuthenticationSettings = true;
              if (this.samlRequired) {
                //if we have a saml required situation, then we should just use the url from the last account found.
                window.location.replace(this.ssoLoginUrl);
                return;
              } else {
                this.canLoginWithPassword = true;
              }
            }
          }),
        )
        .subscribe(noop);
    }
  }

  clearAuthenticationSettings() {
    this.hasRetrievedAuthenticationSettings = false;
  }

  togglePasswordVisibility(): void {
    this.passwordFieldElement.type = this.passwordFieldElement.type === 'password' ? 'text' : 'password';
  }

  private _showError(errorMsg?: string): void {
    this.message = {
      appearance: 'outline',
      content: errorMsg || 'Invalid Credentials',
      shake: true,
      showIcon: false,
      type: 'error',
    };
  }
}
